<template>
  <div class="pp" :class="temaClass">

    <!-- Loading -->
    <div v-if="cargando" class="pp-loading">
      <div class="pp-loading__spinner"></div>
    </div>

    <!-- Error -->
    <div v-else-if="errorCodigo" class="pp-error">
      <div class="pp-error__box">
        <div class="pp-error__icon">🔒</div>
        <h2 class="pp-error__titulo">{{ errorMensaje }}</h2>
        <p class="pp-error__sub">Si crees que es un error, contacta con el tipster.</p>
      </div>
    </div>

    <!-- Pasarela activa -->
    <template v-else-if="pasarela">

      <!-- Decoraciones de fondo -->
      <div class="pp-deco pp-deco--bl" aria-hidden="true"></div>
      <div class="pp-deco pp-deco--tr" aria-hidden="true"></div>

      <!-- Banner -->
      <div v-if="pasarela.banner_url" class="pp-banner">
        <img :src="urlArchivo(pasarela.banner_url)" :alt="tipster.nombre" class="pp-banner__img" />
      </div>

      <!-- Header -->
      <header class="pp-header">
        <div class="pp-header__inner">
          <img
            v-if="pasarela.logo_url"
            :src="urlArchivo(pasarela.logo_url)"
            :alt="tipster.nombre"
            class="pp-header__logo"
          />
          <div class="pp-header__text">
            <h1 class="pp-header__titulo">{{ pasarela.titulo || tipster.nombre }}</h1>
            <p v-if="pasarela.descripcion" class="pp-header__desc">{{ pasarela.descripcion }}</p>
          </div>
        </div>
      </header>

      <main class="pp-main">

        <!-- Bloque: productos destacados -->
        <section v-if="productosDestacados.length" class="pp-bloque">
          <div
            v-for="p in productosDestacados"
            :key="p.id"
            class="pp-fila"
          >
            <div class="pp-fila__icono">
              <i class="fas fa-star"></i>
            </div>
            <div class="pp-fila__info">
              <span class="pp-fila__nombre">{{ p.nombre }}</span>
              <span v-if="p.descripcion" class="pp-fila__desc">{{ p.descripcion }}</span>
            </div>
            <div class="pp-fila__precios">
              <span v-if="p.precio_rebajado" class="pp-fila__precio-old">{{ formatPrecio(p.precio) }}</span>
              <span class="pp-fila__precio-new">{{ formatPrecio(p.precio_rebajado ?? p.precio) }}</span>
            </div>
            <a :href="urlPago(p.url_pago)" class="pp-fila__btn">
              {{ pasarela.texto_boton || "Comprar" }}
            </a>
          </div>
        </section>

        <!-- Divisor si hay ambos grupos -->
        <div v-if="productosDestacados.length && productosNormales.length" class="pp-divisor">
          <span class="pp-divisor__linea"></span>
          <span class="pp-divisor__icono"><i class="fas fa-star"></i></span>
          <span class="pp-divisor__linea"></span>
          <span class="pp-divisor__label">Otros Servicios</span>
        </div>

        <!-- Bloque: productos regulares -->
        <section v-if="productosNormales.length" class="pp-bloque">
          <div
            v-for="p in productosNormales"
            :key="p.id"
            class="pp-fila"
          >
            <div class="pp-fila__icono">
              <i class="fas fa-trophy"></i>
            </div>
            <div class="pp-fila__info">
              <span class="pp-fila__nombre">{{ p.nombre }}</span>
              <span v-if="p.descripcion" class="pp-fila__desc">{{ p.descripcion }}</span>
            </div>
            <div class="pp-fila__precios">
              <span v-if="p.precio_rebajado" class="pp-fila__precio-old">{{ formatPrecio(p.precio) }}</span>
              <span class="pp-fila__precio-new">{{ formatPrecio(p.precio_rebajado ?? p.precio) }}</span>
            </div>
            <a :href="urlPago(p.url_pago)" class="pp-fila__btn">
              {{ pasarela.texto_boton || "Comprar" }}
            </a>
          </div>
        </section>

      </main>

      <footer class="pp-footer">Powered by <strong>Quanty</strong></footer>

    </template>
  </div>
</template>

<script>
import { obtenerPasarelaPublica } from "@/services/pasarelas.service.js";

export default {
  name: "PasarelaPublica",
  data() {
    return {
      cargando: true,
      pasarela: null,
      tipster: null,
      productos: [],
      errorCodigo: null,
      errorMensaje: null,
    };
  },
  computed: {
    temaClass() {
      return this.pasarela ? `theme-${this.pasarela.tema_visual}` : "";
    },
    productosDestacados() {
      return this.productos.filter((p) => p.destacado);
    },
    productosNormales() {
      return this.productos.filter((p) => !p.destacado);
    },
  },
  created() {
    if (this.$store.state.showNavbar) {
      this.$store.commit("toggleEveryDisplay");
      this._layoutOculto = true;
    }
  },
  beforeUnmount() {
    if (this._layoutOculto) {
      this.$store.commit("toggleEveryDisplay");
    }
  },
  beforeMount() {
    this.$store.commit('setShowSidenav', false);
    this.$store.commit('setShowNavbar', true);
    this.$store.commit('setShowFooter', true);
  },

  async mounted() {
    const slug = this.$route.params.slug;
    // Capturar fbclid y UTMs para pasarlos a los links de pago
    const params = new URLSearchParams(window.location.search);
    this._metaParams = {};
    ['fbclid','utm_source','utm_medium','utm_campaign','utm_content','utm_term'].forEach(k => {
      if (params.get(k)) this._metaParams[k] = params.get(k);
    });
    try {
      const res = await obtenerPasarelaPublica(slug);
      this.pasarela = res.data.pasarela;
      this.tipster  = res.data.tipster;
      this.productos = res.data.productos;
    } catch (e) {
      const data = e?.response?.data;
      this.errorCodigo  = data?.codigo  || "error";
      this.errorMensaje = data?.mensaje  || "Pasarela no disponible";
    } finally {
      this.cargando = false;
    }
  },
  methods: {
    urlPago(base) {
      if (!base || !Object.keys(this._metaParams || {}).length) return base;
      const sep = base.includes('?') ? '&' : '?';
      return base + sep + new URLSearchParams(this._metaParams).toString();
    },
    formatPrecio(precio) {
      return Number(precio).toLocaleString("es-ES", { style: "currency", currency: "EUR" });
    },
    urlArchivo(url) {
      if (!url) return null;
      if (url.startsWith("http")) return url;
      return process.env.VUE_APP_API_BASE_URL + url;
    },
  },
};
</script>

<style scoped>
/* ════════════════════════════════════════════════
   BASE
════════════════════════════════════════════════ */
*, *::before, *::after { box-sizing: border-box; }

.pp {
  min-height: 100vh;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", sans-serif;
  -webkit-font-smoothing: antialiased;
  position: relative;
  overflow-x: hidden;
}

/* ════════════════════════════════════════════════
   DECORACIONES DE FONDO
════════════════════════════════════════════════ */
.pp-deco {
  position: fixed;
  border-radius: 50%;
  pointer-events: none;
  z-index: 0;
  background: var(--acento, #4caf50);
}
.pp-deco--bl {
  width: 520px; height: 520px;
  bottom: -260px; left: -180px;
  opacity: 0.08;
}
.pp-deco--tr {
  width: 360px; height: 360px;
  top: -160px; right: -120px;
  opacity: 0.06;
}

/* ════════════════════════════════════════════════
   LOADING
════════════════════════════════════════════════ */
.pp-loading {
  display: flex; align-items: center; justify-content: center;
  min-height: 100vh;
}
.pp-loading__spinner {
  width: 48px; height: 48px;
  border: 3px solid var(--q-border);
  border-top-color: var(--acento, #4caf50);
  border-radius: 50%;
  animation: pp-spin 0.8s linear infinite;
}
@keyframes pp-spin { to { transform: rotate(360deg); } }

/* ════════════════════════════════════════════════
   ERROR
════════════════════════════════════════════════ */
.pp-error {
  display: flex; align-items: center; justify-content: center;
  min-height: 100vh; padding: 2rem; position: relative; z-index: 1;
}
.pp-error__box { text-align: center; max-width: 420px; }
.pp-error__icon { font-size: 3.5rem; margin-bottom: 1.25rem; }
.pp-error__titulo { font-size: 1.5rem; font-weight: 800; margin-bottom: 0.5rem; color: var(--q-text) !important; }
.pp-error__sub { color: var(--q-muted) !important; font-size: 0.9rem; line-height: 1.6; }

/* ════════════════════════════════════════════════
   BANNER
════════════════════════════════════════════════ */
.pp-banner {
  position: relative; z-index: 1;
  width: 100%;
}
.pp-banner__img {
  width: 100%; height: auto;
  max-height: 400px;
  object-fit: cover; object-position: center;
  display: block;
}

/* ════════════════════════════════════════════════
   HEADER
════════════════════════════════════════════════ */
.pp-header {
  position: relative; z-index: 1;
  padding: 2.5rem 1.5rem 1.5rem;
}
.pp-header__inner {
  max-width: 720px; margin: 0 auto;
  display: flex; align-items: center; gap: 1.5rem; flex-wrap: wrap;
}
.pp-header__logo {
  height: 80px; width: auto; max-width: 160px;
  object-fit: contain;
}
.pp-header__titulo {
  font-size: 2.4rem; font-weight: 900;
  margin: 0; line-height: 1.1;
  letter-spacing: -0.03em;
  color: var(--q-text) !important;
}
.pp-header__desc {
  margin: 0.4rem 0 0;
  font-size: 0.95rem; color: var(--q-muted) !important;
  line-height: 1.5;
}

/* ════════════════════════════════════════════════
   MAIN
════════════════════════════════════════════════ */
.pp-main {
  position: relative; z-index: 1;
  padding: 0 1.5rem 3rem;
  max-width: 720px; margin: 0 auto;
}

/* ════════════════════════════════════════════════
   BLOQUE (contenedor de filas)
════════════════════════════════════════════════ */
.pp-bloque {
  background: var(--q-card);
  border-radius: 18px;
  box-shadow: 0 4px 24px rgba(0,0,0,0.08);
  overflow: hidden;
  margin-bottom: 1rem;
}

/* ════════════════════════════════════════════════
   FILA DE PRODUCTO
════════════════════════════════════════════════ */
.pp-fila {
  display: flex;
  align-items: center;
  gap: 1.25rem;
  padding: 1.1rem 1.5rem;
  border-bottom: 1px solid var(--q-border);
  transition: background 0.15s;
}
.pp-fila:last-child { border-bottom: none; }
.pp-fila:hover { background: var(--q-hover); }

.pp-fila__icono {
  width: 48px; height: 48px;
  border-radius: 50%;
  border: 2px solid var(--acento, #4caf50);
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
  color: var(--acento, #4caf50);
  font-size: 1.1rem;
}

.pp-fila__info {
  flex: 1; min-width: 0;
  display: flex; flex-direction: column; gap: 0.2rem;
}
.pp-fila__nombre {
  font-size: 1rem; font-weight: 700;
  color: var(--q-text) !important;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.pp-fila__desc {
  font-size: 0.8rem; color: var(--q-muted) !important;
  line-height: 1.4;
}
.pp-fila__desc * { margin: 0; color: inherit !important; }

.pp-fila__precios {
  display: flex; align-items: baseline; gap: 0.5rem;
  flex-shrink: 0; white-space: nowrap;
}
.pp-fila__precio-old {
  font-size: 0.85rem; color: var(--q-muted) !important;
  text-decoration: line-through;
}
.pp-fila__precio-new {
  font-size: 1.4rem; font-weight: 900;
  color: var(--acento, #4caf50) !important;
  letter-spacing: -0.02em;
}

.pp-fila__btn {
  flex-shrink: 0;
  padding: 0.6rem 1.4rem;
  border-radius: 10px;
  font-weight: 700; font-size: 0.9rem;
  text-decoration: none;
  color: #fff !important;
  background: var(--acento, #4caf50);
  transition: filter 0.15s, transform 0.15s;
  white-space: nowrap;
  box-shadow: 0 3px 12px rgba(0,0,0,0.15);
}
.pp-fila__btn:hover {
  filter: brightness(1.1);
  transform: translateY(-1px);
  color: #fff !important;
}

/* ════════════════════════════════════════════════
   DIVISOR
════════════════════════════════════════════════ */
.pp-divisor {
  display: flex; align-items: center; gap: 0.75rem;
  margin: 1.5rem 0 1rem; flex-wrap: wrap;
}
.pp-divisor__linea {
  flex: 1; height: 2px;
  background: linear-gradient(to right, transparent, var(--acento, #4caf50), transparent);
  opacity: 0.4;
}
.pp-divisor__icono {
  width: 36px; height: 36px;
  border-radius: 50%;
  background: var(--acento, #4caf50);
  color: #fff;
  display: flex; align-items: center; justify-content: center;
  font-size: 0.85rem;
  box-shadow: 0 2px 10px rgba(0,0,0,0.15);
}
.pp-divisor__label {
  font-size: 0.8rem; font-weight: 700;
  letter-spacing: 0.08em; text-transform: uppercase;
  color: var(--q-muted) !important;
  width: 100%; text-align: center;
  margin-top: 0.25rem;
}

/* ════════════════════════════════════════════════
   FOOTER
════════════════════════════════════════════════ */
.pp-footer {
  position: relative; z-index: 1;
  text-align: center; padding: 1.5rem;
  font-size: 0.78rem; color: var(--q-muted) !important;
  letter-spacing: 0.03em;
}

/* ════════════════════════════════════════════════
   TEMAS — solo cambian variables de color
════════════════════════════════════════════════ */

/* verde_pro */
.theme-verde_pro {
  --acento: #2e7d32;
  background: linear-gradient(160deg, #070e0a 0%, #060d08 100%);
}

/* dark_premium */
.theme-dark_premium {
  --acento: #f9a825;
  background: linear-gradient(160deg, #0d0c05 0%, #0b0a03 100%);
}
.theme-dark_premium .pp-fila__btn { color: var(--q-gold-on) !important; }

/* azul_quanty */
.theme-azul_quanty {
  --acento: #0288d1;
  background: linear-gradient(160deg, #06090f 0%, #060b12 100%);
}

/* claro_deportivo */
.theme-claro_deportivo {
  --acento: #1565c0;
  background: linear-gradient(160deg, #06080e 0%, #06090f 100%);
}

/* rojo_energia */
.theme-rojo_energia {
  --acento: #c62828;
  background: linear-gradient(160deg, #0e0606 0%, #0c0505 100%);
}

/* violeta_vip */
.theme-violeta_vip {
  --acento: #7b1fa2;
  background: linear-gradient(160deg, #0c060f 0%, #0a040d 100%);
}

/* ════════════════════════════════════════════════
   RESPONSIVE
════════════════════════════════════════════════ */
@media (max-width: 600px) {
  .pp-header__titulo { font-size: 1.75rem; }
  .pp-fila {
    flex-wrap: wrap;
    gap: 0.75rem;
  }
  .pp-fila__precios { margin-left: auto; }
  .pp-fila__btn { width: 100%; text-align: center; }
  .pp-fila__nombre { white-space: normal; }
}
</style>
